import {
  Badge,
  Box,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core"
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  TruckIcon,
} from "@phosphor-icons/react"
import { useMemo, useState } from "react"

export interface VehicleOption {
  id: string
  plate: string
  model: string
  vehicleClass: string
  vehicleClassLabel: string
  maxSize: number
  status?: "AVAILABLE" | "ON_DUTY" | null
}

interface Props {
  vehicles: VehicleOption[]
  selectedId: string | null
  onChange: (id: string) => void
  minSize?: number
  loading?: boolean
}

function statusBadge(status: VehicleOption["status"]) {
  if (status === "AVAILABLE")
    return <Badge size="xs" color="green" variant="light">Бэлэн</Badge>
  if (status === "ON_DUTY")
    return <Badge size="xs" color="yellow" variant="light">Ажилтай</Badge>
  return null
}

export function VehiclePickerList({
  vehicles,
  selectedId,
  onChange,
  minSize,
  loading,
}: Props) {
  const [search, setSearch] = useState("")
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return vehicles
    return vehicles.filter(
      (v) =>
        v.plate.toLowerCase().includes(q) ||
        v.model.toLowerCase().includes(q),
    )
  }, [vehicles, search])

  const title = minSize
    ? `Тээврийн хэрэгсэл (≥ ${minSize}S багтаамжтай)`
    : "Тээврийн хэрэгсэл"

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <Text size="sm" fw={700}>
          {title}{" "}
          <Text component="span" size="sm" c="dimmed" fw={500}>
            ({vehicles.length})
          </Text>
        </Text>
      </Group>
      <TextInput
        size="sm"
        placeholder="Улсын дугаар / марк хайх..."
        leftSection={<MagnifyingGlassIcon size={14} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />
      <ScrollArea h={180} type="auto">
        <Stack gap={4}>
          {loading && <Loader size="xs" />}
          {!loading && filtered.length === 0 && (
            <Text size="sm" c="dimmed">
              Олдсонгүй
            </Text>
          )}
          {filtered.map((v) => {
            const isSelected = v.id === selectedId
            const isUndersized = minSize ? v.maxSize < minSize : false
            return (
              <Box
                key={v.id}
                onClick={() => !isUndersized && onChange(v.id)}
                p="xs"
                style={{
                  cursor: isUndersized ? "not-allowed" : "pointer",
                  opacity: isUndersized ? 0.5 : 1,
                  borderRadius: "var(--mantine-radius-sm)",
                  background: isSelected
                    ? "var(--mantine-color-brand-0)"
                    : "transparent",
                  borderLeft: isSelected
                    ? "3px solid var(--mantine-color-brand-6)"
                    : "3px solid transparent",
                }}
              >
                <Group gap="sm" wrap="nowrap">
                  <TruckIcon size={20} color="var(--mantine-color-gray-7)" />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" fw={700}>
                        {v.plate}
                      </Text>
                      <Badge size="xs" color="blue" variant="light">
                        {v.maxSize}S
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {v.model} · {v.vehicleClassLabel}
                    </Text>
                  </Box>
                  <Group gap={4} wrap="nowrap">
                    {statusBadge(v.status ?? null)}
                    {isSelected && (
                      <CheckCircleIcon size={16} weight="fill" color="var(--mantine-color-brand-6)" />
                    )}
                  </Group>
                </Group>
              </Box>
            )
          })}
        </Stack>
      </ScrollArea>
    </Stack>
  )
}
